import React, { useState, useEffect, useRef } from "react";
import Dynamic_PR_table from "../../../components/Dynamic_PR_table";
import { API_URL } from "../../../constants/index";
import { GetServiceCaller } from "../../../services/serviceCaller";
import { useDispatch } from "react-redux";
import { updateNavbarHeading } from "../../../constants/layout/store/filterSlice";
import moment from "moment";

const EmployeeMasterReport = () => {
  const dispatch = useDispatch();
  const requestCounterRef = useRef(0); // Stable across renders

  // State Management
  const [searchTerm, setSearchTerm] = useState("");
  const [empDataMaster, setEmpMasterReport] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [projectList, setProjectList] = useState([]);
  const [apiStatus, SetApiStatus] = useState("");
  const [totalRevenue, setTotalRevenue] = useState();

  // Initialize filter state with default checked value for leaves
  const [filterFormData, setFilterData] = useState([
    { label: "Consider Leaves in Report", value: true },
  ]);

  useEffect(() => {
    fetchFilterOptions();
    // Assuming updateNavbarHeading is an action creator imported into your project
    // dispatch(updateNavbarHeading("Revenue Master Report"));
  }, []);

  // Table Columns Definition
  const columns = {
    EmployeeCode: { text: "EMP ID", align: "start", visible: true },
    EmployeeStatus: { text: "Employee Status", align: "start", visible: true },
    EmployeeName: { text: "Employee Name", visible: true },
    DOJ: { text: "DOJ", align: "center", visible: true },
    DOB: { text: "DOB", align: "center", visible: true },
    ReportingManager: { text: "Reporting Manager", visible: true },
    PracticeManager: { text: "Practice Manager", visible: true },
    HiringType: { text: "Hiring Type", visible: true },
    TotalExperience: { text: "Total Exp.", align: "center", visible: true },
    ReleventExperience: { text: "Relevent Exp.", align: "center", visible: true },
    PresentLocation: { text: "Present Location", visible: true },
    Flexibility: { text: "Flexibility", visible: true },
    PreferredLocationIfYes: { text: "Preferred Location", visible: true },
    WorkMode: { text: "Work Mode", visible: true },
    Gross: { text: "Gross", align: "end", visible: true },
    PrimarySkill: { text: "Primary Skill", visible: true },
    SecondarySkill: { text: "Secondary Skill", visible: true },
    Category: { text: "Category", visible: true },
    Bilability: { text: "Bilability", visible: true },
    ClientName: { text: "Client Name", visible: true },
    ProjectName: { text: "Project Name", visible: true },
    AccountManager: { text: "Account Manager", visible: true },
    StartDate: { text: "Start Date", align: "center", visible: true },
    ToDate: { text: "To Date", align: "center", visible: true },
    ActualEndDate: { text: "Actual End Date", align: "center", visible: true },
    BillingRate: { text: "Billing Rate", align: "right", visible: true },
    Currency: { text: "Currency", align: "right", visible: true },
    BillFrequency: { text: "Bill Frequency", align: "right", visible: true },
    ProjectType: { text: "Project Type", align: "right", visible: true },
    FixedBid: { text: "Fixed Bid", align: "right", visible: true },
    LeavesTaken: { text: "Leaves Taken", align: "right", visible: true },
    Revenue: { text: "Revenue", align: "right", visible: true },
  };


const generateMonthOptions = () => {
  const options = [];
  
  // 1. Start exactly 12 months in the past
  const startDate = moment().subtract(12, "months");

  // 2. Loop 25 times to cover: 12 months past + 1 current month + 12 months future
  for (let i = 0; i < 25; i++) {
    // Clone the start date and add 'i' months sequentially
    const date = moment(startDate).add(i, "months");
    
    options.push({
      value: date.format("YYYY-MM"),   // Stored internally (e.g., "2025-06", "2026-12", "2027-06")
      label: date.format("MMMM YYYY"), // Displayed to user (e.g., "June 2025", "December 2026")
    });
  }
  
  return options;
};
  // Change Handler mapping selections to filterFormData array structure
  const handleChange = (e, name) => {
    setFilterData((prevFormData) => {
      const existingLabelIndex = prevFormData.findIndex(
        (item) => item.label === name
      );

      // Distinguish if incoming argument is React-Select wrapper object, event value, or straight payload array
      let extractedValue;
      if (e && e.value !== undefined) {
        extractedValue = e.value;
      } else {
        extractedValue = e;
      }

      if (existingLabelIndex !== -1) {
        return prevFormData.map((item, index) =>
          index === existingLabelIndex
            ? { ...item, value: extractedValue }
            : item
        );
      } else {
        return [
          ...prevFormData,
          {
            label: name,
            value: extractedValue,
          },
        ];
      }
    });
  };

  // Dynamic layout fields configurations mapped into Dynamic_FilterForm mapping layer
  const formDatas = [
    { name: "Employee", type: "Select", options: [...employeeList] },
    { 
      name: "Select Month", 
      type: "IsMulti", // Leverages the prebuilt multi-select code branch inside your Child Component
      options: generateMonthOptions() 
    },
    { name: "Project", type: "Select", options: [...projectList] },
    { name: "Consider Leaves in Report", type: "Checkbox" },
    { name: "Client", type: "Select", options: [...clientList] },
  ];

  // Fetch initial option selectors setup parameters
  const fetchFilterOptions = async () => {
    try {
      const response = await GetServiceCaller(`${API_URL}employeemaster/filter-list`);

      if (response?.status === 200) {
        const cleanedData = response?.data?.data;

        const empList = cleanedData?.employeelist?.map((tech) => ({
          value: tech?.value || "",
          label: tech?.text || "",
        })) || [];
        setEmployeeList(empList);

        const clientList = cleanedData?.clientName?.map((tech) => ({
          value: tech?.value || "",
          label: tech?.text || "",
        })) || [];
        setClientList(clientList);

        const projectTypeData = cleanedData?.projecttype || [];
        const projList = projectTypeData.map((tech) => ({
          value: tech.value,
          label: tech.text,
        }));
        setProjectList(projList);
      }
    } catch (error) {
      console.error("Fetching Filter data Api Error:", error);
    }
  };

  // Process data parameters filter lists parsing logic & dispatch request execution
  const handleGetData = async () => {
    SetApiStatus(undefined);
    setEmpMasterReport([]);

    const currentRequestId = ++requestCounterRef.current; // Increment Request ID tracking safe checks

    try {
      // selectMonths will retrieve an array of value strings directly from the IsMulti field type mapping: e.g. ["2026-05", "2026-06"]
      const selectMonths = filterFormData.find((o) => o.label === "Select Month")?.value;
      const projectType = filterFormData.find((o) => o.label === "Project")?.value;
      const employee = filterFormData.find((o) => o.label === "Employee")?.value;
      const client = filterFormData.find((o) => o.label === "Client")?.value;
      const leavesTaken = filterFormData.find((o) => o.label === "Consider Leaves in Report")?.value;

      // Transform arrays securely to match expected API query configurations
      let formattedMonthsString = "";
      if (Array.isArray(selectMonths)) {
        formattedMonthsString = selectMonths.filter(Boolean).join(",");
      } else if (selectMonths) {
        formattedMonthsString = moment(selectMonths).format("YYYY-MM");
      }

      const leavesTakenValue = leavesTaken === true ? 1 : 0;

      // Make API Network requests with compiled comma-delimited strings
      const response = await GetServiceCaller(
        `${API_URL}rom-report/13?` +
        `P_StartMonth=${formattedMonthsString}` + // Becomes string syntax structure payload: "2026-05,2026-06"
        `&P_ProjectType=${projectType || ""}` +
        `&P_Employee=${employee || ""}` +
        `&P_LeavesTaken=${leavesTakenValue}` +
        `&P_ClientID=${client || ""}`
      );

      // Clean check avoiding async rendering overlaps condition states
      if (currentRequestId !== requestCounterRef.current) return;

      SetApiStatus(response?.status);

      if (response.status === 200) {
        const totReven = response?.data?.data?.totalRevenue;
        setTotalRevenue(Number(parseFloat(totReven).toFixed(2)));

        const empData = response?.data?.data?.data;
        setEmpMasterReport(
          empData?.map((o) => ({
            ...o,
            Gross: Number(o.Gross).toFixed(2),
          }))
        );

        // Optional update sequences sync updates fallback rules tracking items
        const empList = response?.data?.data?.employee?.map((tech) => ({
          value: tech?.value || "",
          label: tech?.text || "",
        })) || [];
        setEmployeeList(empList);

        const projectTypeData = response?.data?.data?.projecttype || [];
        const projList = projectTypeData.map((tech) => ({
          value: tech.value,
          label: tech.text,
        }));
        setProjectList(projList);
      } else {
        console.error("Failed to fetch report:", response.status);
      }
    } catch (error) {
      if (currentRequestId === requestCounterRef.current) {
        console.error("Error fetching report", error);
      }
    }
  };

  const handleReset = () => {
    setFilterData([]);
    setEmpMasterReport([]);
  };

  const ReadOnlyValues = [
    {
      label: "Total Revenue",
      value: totalRevenue || 0,
    },
  ];

  return (
    <>
      <div className="container-fluid" id="skill_section">
        <div className="container-fluid" id="skill-table-area">
          <div className="row">
            <Dynamic_PR_table
              data={empDataMaster}
              ReadOnlyValues={ReadOnlyValues}
              columns={columns}
              itemsPerPage={10}
              isSorting={true}
              showIcon1={true}
              isApiStatus={apiStatus}
              handleReset={handleReset}
              isRequiredStatus={true}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              formDatas={formDatas}
              isFilterRequired={true}
              filterFormData={filterFormData}
              handleGetData={handleGetData}
              handleChange={handleChange}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default EmployeeMasterReport;
